import { useEffect, useState, type ReactNode } from 'react';

import { api } from './api';

/**
 * Construtor de filtros — compartilhado.
 *
 * Vive fora de qualquer tela porque **duas** o usam: a regra do Sentinela e o
 * cartão do painel. Os dois manipulam a mesma estrutura (`FiltroConsulta` do
 * domínio) e são compilados pelo mesmo código no servidor; ter duas cópias da
 * interface era garantir que uma ficasse para trás.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 **Os campos vêm do servidor, e é por isso que vieram.**
 *
 * Medido em 24/09/2026, usando o produto: a lista abaixo era escrita à mão e
 * oferecia **23 dos 38** campos base. `patch_verificado_em` estava fora — o
 * campo que responde *"quais máquinas não têm detecção de patch"*, que no
 * laboratório eram 18 de 25. O compilador sabia consultá-lo; a regra do
 * Sentinela o usa e funciona. Só o `<select>` não o mostrava.
 *
 * Nada quebrava: tipos aprovavam, testes passavam. O sintoma era um menu mais
 * curto que o produto, e ninguém liga um menu curto a uma lista desatualizada
 * meses depois.
 *
 * ⚠️ **Não bastava acrescentar os que faltavam.** Metade do catálogo são campos
 * de regra de inventário personalizado (CIR), e eles **variam por instalação**:
 * fixá-los aqui mostraria a um cliente os campos coletados no parque de outro.
 * Por isso a lista passou a ser buscada em `/api/inventario/campos`, que compõe
 * a base do domínio com o que ESTA organização autorizou.
 *
 * A lista fixa continua aqui como **reserva** — se a busca falhar, o editor
 * abre com o vocabulário essencial em vez de um `<select>` vazio.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface Filtro {
  campo: string;
  operador: string;
  valor: string;
}

/** Reserva: o vocabulário essencial, para quando a busca ao servidor falhar. */
const CAMPOS_RESERVA: readonly { readonly valor: string; readonly rotulo: string }[] = [
  { valor: 'nome', rotulo: 'Nome do dispositivo' },
  { valor: 'versao_windows', rotulo: 'Versão do Windows' },
  { valor: 'so_familia', rotulo: 'Família do sistema' },
  { valor: 'so_nome', rotulo: 'Nome do sistema' },
  { valor: 'so_build', rotulo: 'Build do sistema' },
  { valor: 'hw_fabricante', rotulo: 'Fabricante' },
  { valor: 'hw_modelo', rotulo: 'Modelo' },
  { valor: 'hw_ram_mb', rotulo: 'Memória (MB)' },
  { valor: 'hw_cpu_modelo', rotulo: 'Processador' },
  { valor: 'hw_tpm_versao', rotulo: 'TPM' },
  { valor: 'hw_secure_boot', rotulo: 'Secure Boot' },
  { valor: 'hw_bitlocker', rotulo: 'BitLocker' },
  { valor: 'hw_eh_virtual', rotulo: 'É virtual' },
  { valor: 'rotulos', rotulo: 'Rótulo do KACE' },
  { valor: 'patches_criticos_pendentes', rotulo: 'Patches críticos pendentes' },
  { valor: 'patches_pendentes', rotulo: 'Patches pendentes' },
  { valor: 'patches_com_erro', rotulo: 'Patches que falharam' },
  { valor: 'garantia_fim', rotulo: 'Fim da garantia' },
  { valor: 'usuario_logado', rotulo: 'Usuário' },
  { valor: 'dominio', rotulo: 'Domínio' },
  { valor: 'ip', rotulo: 'IP' },
  { valor: 'versao_agente', rotulo: 'Versão do agente' },
  { valor: 'ultimo_inventario', rotulo: 'Último inventário' },
];

/**
 * Campos de chamado. Espelha `CAMPOS_CHAMADO` do domínio.
 *
 * Os derivados ("Dias em aberto", "Horas sem movimentação") aparecem aqui como
 * campo normal de propósito: é assim que a pergunta nasce na cabeça de quem
 * opera. Obrigar a escolher uma data absoluta faria a regra envelhecer sozinha
 * e parar de valer sem ninguém perceber.
 */
const CAMPOS_CHAMADO_RESERVA: readonly { readonly valor: string; readonly rotulo: string }[] = [
  { valor: 'titulo', rotulo: 'Título' },
  { valor: 'descricao', rotulo: 'Descrição' },
  { valor: 'fila_nome', rotulo: 'Fila' },
  { valor: 'estado', rotulo: 'Estado' },
  { valor: 'status_nome', rotulo: 'Status' },
  { valor: 'categoria_nome', rotulo: 'Categoria' },
  { valor: 'prioridade_nome', rotulo: 'Prioridade' },
  { valor: 'impacto_nome', rotulo: 'Impacto' },
  { valor: 'solicitante_nome', rotulo: 'Solicitante' },
  { valor: 'responsavel_nome', rotulo: 'Responsável' },
  { valor: 'resolucao', rotulo: 'Resolução' },
  { valor: 'numero', rotulo: 'Número' },
  { valor: 'dias_aberto', rotulo: 'Dias em aberto' },
  { valor: 'horas_sem_movimentacao', rotulo: 'Horas sem movimentação' },
  { valor: 'horas_para_vencer', rotulo: 'Horas para vencer o SLA' },
  { valor: 'tem_responsavel', rotulo: 'Tem responsável' },
  { valor: 'tem_resolucao', rotulo: 'Tem resolução escrita' },
  { valor: 'esta_aberto', rotulo: 'Está aberto' },
  { valor: 'criado_em', rotulo: 'Aberto em' },
  { valor: 'atualizado_em', rotulo: 'Última alteração' },
  { valor: 'fechado_em', rotulo: 'Fechado em' },
];

const OPERADORES: readonly { readonly valor: string; readonly rotulo: string }[] = [
  { valor: 'eq', rotulo: 'é igual a' },
  { valor: 'ne', rotulo: 'é diferente de' },
  { valor: 'contem', rotulo: 'contém' },
  { valor: 'nao_contem', rotulo: 'não contém' },
  { valor: 'comeca_com', rotulo: 'começa com' },
  { valor: 'termina_com', rotulo: 'termina com' },
  { valor: 'gt', rotulo: 'é maior que' },
  { valor: 'gte', rotulo: 'é maior ou igual a' },
  { valor: 'lt', rotulo: 'é menor que' },
  { valor: 'lte', rotulo: 'é menor ou igual a' },
  { valor: 'em', rotulo: 'está na lista (separada por vírgula)' },
  { valor: 'existe', rotulo: 'está preenchido' },
  { valor: 'nao_existe', rotulo: 'está vazio' },
  { valor: 'ha_mais_de_dias', rotulo: 'há mais de N dias' },
  { valor: 'ha_menos_de_dias', rotulo: 'há menos de N dias' },
];

/** Operadores que não usam valor — o campo some para não confundir. */
const SEM_VALOR = new Set(['existe', 'nao_existe']);

interface CampoOferecido {
  readonly valor: string;
  readonly rotulo: string;
}

interface Catalogo {
  readonly dispositivo: readonly CampoOferecido[];
  readonly chamado: readonly CampoOferecido[];
}

const RESERVA: Catalogo = { dispositivo: CAMPOS_RESERVA, chamado: CAMPOS_CHAMADO_RESERVA };

/**
 * Uma busca por sessão, compartilhada pelas duas telas.
 *
 * ⚠️ A promessa é guardada, não o resultado: o editor de regra e o de cartão
 * podem montar ao mesmo tempo, e sem isto seriam duas requisições para a mesma
 * lista. Falha vira reserva e **não** é memorizada como sucesso — a próxima
 * abertura tenta de novo, porque a causa costuma ser sessão expirada ou um
 * reinício do serviço.
 */
let buscaEmCurso: Promise<Catalogo> | null = null;

async function buscarCatalogo(): Promise<Catalogo> {
  const r = await api.get<{
    dispositivo?: readonly { campo: string; rotulo: string }[];
    chamado?: readonly { campo: string; rotulo: string }[];
  }>('/api/inventario/campos');

  const converter = (
    lista: readonly { campo: string; rotulo: string }[] | undefined,
    reserva: readonly CampoOferecido[],
  ): readonly CampoOferecido[] =>
    lista === undefined || lista.length === 0
      ? reserva
      : lista.map((c) => ({ valor: c.campo, rotulo: c.rotulo }));

  return {
    dispositivo: converter(r.dispositivo, CAMPOS_RESERVA),
    chamado: converter(r.chamado, CAMPOS_CHAMADO_RESERVA),
  };
}

function useCatalogoDeCampos(): Catalogo {
  const [catalogo, setCatalogo] = useState<Catalogo>(RESERVA);

  useEffect(() => {
    let vivo = true;
    if (buscaEmCurso === null) {
      buscaEmCurso = buscarCatalogo().catch((e: unknown) => {
        // Sem memorizar a falha: a próxima montagem tenta outra vez.
        buscaEmCurso = null;
        throw e;
      });
    }
    void buscaEmCurso.then(
      (c) => {
        if (vivo) setCatalogo(c);
      },
      () => {
        /* Fica na reserva. Um editor com o vocabulário essencial é melhor
           que um `<select>` vazio, e melhor que uma tela de erro por um
           menu. */
      },
    );
    return () => {
      vivo = false;
    };
  }, []);

  return catalogo;
}

/**
 * O filtro que nasce ao clicar "acrescentar".
 *
 * ⚠️ O campo padrão precisa existir no vocabulário do alvo. `versao_windows`
 * numa regra de chamado não compila — e o `<select>` mostraria o primeiro
 * campo da lista enquanto o estado guardava outro, que é o tipo de divergência
 * que só aparece ao salvar.
 */
export const filtroVazio = (alvo: 'dispositivo' | 'chamado' = 'dispositivo'): Filtro =>
  alvo === 'chamado'
    ? { campo: 'titulo', operador: 'contem', valor: '' }
    : { campo: 'versao_windows', operador: 'eq', valor: '' };

/** Converte o filtro da tela para o formato do domínio. */
export function paraDominio(f: Filtro): Record<string, unknown> {
  if (SEM_VALOR.has(f.operador)) return { campo: f.campo, operador: f.operador, valor: null };
  if (f.operador === 'em') {
    return {
      campo: f.campo,
      operador: f.operador,
      valor: f.valor.split(',').map((x) => x.trim()).filter((x) => x !== ''),
    };
  }
  // Número quando o texto é um número: o compilador distingue campo numérico,
  // mas mandar "8192" como texto num campo de RAM não compila.
  const n = Number(f.valor);
  const numerico = f.valor.trim() !== '' && Number.isFinite(n);
  return { campo: f.campo, operador: f.operador, valor: numerico ? n : f.valor };
}


export function converterDoServidor(v: unknown, obrigatorio = false): Filtro[] {
  if (!Array.isArray(v) || v.length === 0) return obrigatorio ? [filtroVazio()] : [];
  return (v as Record<string, unknown>[]).map((f) => ({
    campo: String(f['campo'] ?? 'nome'),
    operador: String(f['operador'] ?? 'eq'),
    valor: Array.isArray(f['valor'])
      ? (f['valor'] as unknown[]).join(', ')
      : String(f['valor'] ?? ''),
  }));
}


export function ListaDeFiltros({
  titulo,
  ajuda,
  filtros,
  aoMudar,
  alvo = 'dispositivo',
}: {
  titulo: string;
  ajuda: string;
  filtros: Filtro[];
  aoMudar: (f: Filtro[]) => void;
  /** Qual vocabulário oferecer. Chamado e máquina não compartilham campo. */
  alvo?: 'dispositivo' | 'chamado';
}): ReactNode {
  const catalogo = useCatalogoDeCampos();
  const campos = alvo === 'chamado' ? catalogo.chamado : catalogo.dispositivo;

  /**
   * 🔴 Campo salvo que não está na lista continua aparecendo.
   *
   * Uma regra criada quando havia mais CIR autorizada — ou salva enquanto a
   * busca falhava e a reserva estava em uso — guarda um campo que o `<select>`
   * não conhece. Sem esta opção extra, o navegador mostra o PRIMEIRO item da
   * lista enquanto o estado guarda outro: a pessoa abre para mudar o operador
   * e salva um filtro diferente do que leu. É a mesma divergência que o
   * comentário de `filtroVazio` descreve.
   */
  const opcoesDe = (campoAtual: string): readonly CampoOferecido[] =>
    campoAtual === '' || campos.some((c) => c.valor === campoAtual)
      ? campos
      : [...campos, { valor: campoAtual, rotulo: `${campoAtual} (fora do catálogo atual)` }];

  const alterar = (i: number, campo: keyof Filtro, valor: string): void => {
    const novo = filtros.map((f, j) => (i === j ? { ...f, [campo]: valor } : f));
    aoMudar(novo);
  };

  return (
    <div className="bloco-filtros">
      <p className="rotulo-bloco">
        {titulo}
        <span>{ajuda}</span>
      </p>

      {filtros.map((f, i) => (
        <div className="linha-filtro" key={i}>
          <select value={f.campo} onChange={(e) => alterar(i, 'campo', e.target.value)}>
            {opcoesDe(f.campo).map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.rotulo}
              </option>
            ))}
          </select>
          <select
            value={f.operador}
            onChange={(e) => alterar(i, 'operador', e.target.value)}
          >
            {OPERADORES.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.rotulo}
              </option>
            ))}
          </select>
          {SEM_VALOR.has(f.operador) ? (
            <span className="suave" style={{ fontSize: 13 }}>
              (sem valor)
            </span>
          ) : (
            <input
              value={f.valor}
              placeholder={
                f.campo === 'versao_windows'
                  ? 'win10, win11…'
                  : f.campo === 'rotulos'
                    ? 'Back Office'
                    : 'valor'
              }
              onChange={(e) => alterar(i, 'valor', e.target.value)}
            />
          )}
          <button
            className="encolhe"
            title="Remover"
            onClick={() => aoMudar(filtros.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}

      <button onClick={() => aoMudar([...filtros, filtroVazio(alvo)])}>
        + Acrescentar condição
      </button>
      {filtros.length > 1 && (
        <span className="suave" style={{ marginLeft: 10, fontSize: 12.5 }}>
          Todas as condições precisam valer ao mesmo tempo.
        </span>
      )}
    </div>
  );
}

